import { BOOLEAN, STRING } from '../../../utils/component-validations';
import {
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  POSITIONING_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
} from '../../../utils/spds/metadata';
import { tracked } from '@glimmer/tracking';
import Container from './container.gjs';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './button.module.scss';

const METADATA = {
  component: 'Spds::Core::Button',
  description: `
    Base button component for SPDS with flexible layout options and styling

    ### Customization

    The HTML tag for the component can be customized by providing the \`@tag\` argument. In the case that this is set to something other
    than \`button\`, the component will automatically apply the HTML attribute \`role\` with the value \`button\` to the generated HTML
    element.

    This button supports all of our standard button CSS classes (primary, secondary, ghost, etc).

    Wraps the SPDS Core Container component.
  `,
  meta: {
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled']).meta,
    ...pickArgs(CONTAINER_ARGS, [
      'column',
      'fitContent',
      'fitContentHeight',
      'fitContentWidth',
      'grid',
      'reverse',
    ]).meta,
    ...pickArgs(CONTENT_ARGS, ['iconOnly']).meta,
    ...pickArgs(POSITIONING_ARGS, ['absolute', 'fixed']).meta,
    ...FLEX_ARGS.meta,
    ...GRID_ARGS.meta,
    alignItems: {
      ...FLEX_ARGS.meta.alignItems,
      defaultValue: 'center',
    },
    autofocus: {
      defaultValue: false,
      description: 'Automatically focus the button on render',
      type: BOOLEAN,
    },
    error: {
      defaultValue: false,
      description: 'Apply error styling to the button',
      type: BOOLEAN,
    },
    gap: {
      ...FLEX_ARGS.meta.gap,
      defaultValue: '8px',
    },
    inline: {
      defaultValue: false,
      description: 'Display button inline',
      type: BOOLEAN,
    },
    justifyContent: {
      ...FLEX_ARGS.meta.justifyContent,
      defaultValue: 'center',
    },
    size: {
      defaultValue: 'normal',
      description: 'Button size',
      options: ['normal', 'small'],
      type: STRING,
    },
    subgrid: {
      defaultValue: false,
      description: 'Use subgrid for grid template',
      type: BOOLEAN,
    },
    tag: {
      defaultValue: 'button',
      description: 'HTML tag to use for rendering',
      type: STRING,
    },
    text: {
      ...CONTENT_ARGS.meta.text,
      initialValue: 'Button',
    },
    type: {
      defaultValue: 'button',
      description: 'Button type attribute',
      type: STRING,
    },
  },
  name: 'Core Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=19170-11848&m=dev',
    github:
      'https://github.com/simplepractice/ember-simplepractice/tree/staging/addon/src/components/spds/core/button.gjs',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/0/p/56566c-buttons',
  },
};

export default class SpdsCoreButton extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-button';
  classesToMapToArgs = ['iconOnly', 'size'];
  classesToMapToAttrs = ['hasBorder'];
  componentName = 'Spds::Core::Button';
  styles = styles;

  @tracked baseElement;

  get alignItems() {
    return (
      this.args.alignItems ?? getDefaultValueFromMetadata(this.constructor.metadata, 'alignItems')
    );
  }

  get gap() {
    return this.args.gap ?? getDefaultValueFromMetadata(this.constructor.metadata, 'gap');
  }

  get justifyContent() {
    return (
      this.args.justifyContent ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'justifyContent')
    );
  }

  /**
   * determines if role attribute should be set
   * @return {string|undefined} button role attribute value when not a button element
   */
  get role() {
    return this.tag === 'button' ? undefined : 'button';
  }

  get tag() {
    return this.args.tag ?? getDefaultValueFromMetadata(this.constructor.metadata, 'tag');
  }

  get type() {
    return this.args.type ?? getDefaultValueFromMetadata(this.constructor.metadata, 'type');
  }

  /**
   * determines the variant style of the button for data-test attributes
   * @return {string} button variant value
   */
  get variant() {
    if (this.link) return 'link';
    if (this.variantClass) return this.variantClass.split('-').at(-1);

    return this.tag;
  }

  <template>
    <Container
      @absolute={{@absolute}}
      @alignContent={{@alignContent}}
      @alignItems={{this.alignItems}}
      @alignSelf={{@alignSelf}}
      @area={{@area}}
      @areas={{@areas}}
      @autoFlow={{@autoFlow}}
      @column={{@column}}
      @columns={{@columns}}
      @content={{@text}}
      @error={{@error}}
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @fixed={{@fixed}}
      @flex={{@flex}}
      @flexWrap={{@flexWrap}}
      @gap={{this.gap}}
      @grid={{@grid}}
      @gridArea={{@gridArea}}
      @gridAutoColumns={{@gridAutoColumns}}
      @gridAutoFlow={{@gridAutoFlow}}
      @gridAutoRows={{@gridAutoRows}}
      @gridColumn={{@gridColumn}}
      @gridRow={{@gridRow}}
      @gridTemplateAreas={{@gridTemplateAreas}}
      @gridTemplateColumns={{@gridTemplateColumns}}
      @gridTemplateRows={{@gridTemplateRows}}
      @inline={{@inline}}
      @justifyContent={{this.justifyContent}}
      @justifyItems={{@justifyItems}}
      @justifySelf={{@justifySelf}}
      @order={{@order}}
      @reverse={{@reverse}}
      @rows={{@rows}}
      @subgrid={{@subgrid}}
      @tag={{this.tag}}
      class={{this.classes}}
      data-test-spds-button={{this.variant}}
      disabled={{@disabled}}
      role={{this.role}}
      type={{this.type}}
      ...attributes
      {{SpdsAutofocus @autofocus}}
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      {{yield}}
    </Container>
  </template>
}

export const CORE_BUTTON_METADATA = SpdsCoreButton.metadata;
